import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Refresh, FileDownload, ArrowBack, ErrorOutline } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { useSessionValidation } from '../../../utils/sessionValidation';
import LoadingOverlay from '../../common/LoadingOverlay';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const PRIMARY = '#1565c0';
const PRIMARY_DARK = '#0d47a1';
const HEADER_PARTICULARS = '#1a237e';
const HEADER_OPENING = '#4a148c';
const HEADER_DEBIT = '#b71c1c';
const HEADER_CREDIT = '#1b5e20';
const HEADER_CLOSING = '#e65100';

function TrialBalanceAdvancedReport() {
    useSessionValidation();

    const history = useHistory();
    const { companyId, branchId, token, companyName, username } = useAuth();

    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('Loading...');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [validationOpen, setValidationOpen] = useState(false);
    const [validationMsg, setValidationMsg] = useState('');

    const getFinancialYearStart = (date) => {
        const d = new Date(date + 'T00:00:00');
        const month = d.getMonth();
        const year = d.getFullYear();
        const fyStartYear = month < 3 ? year - 1 : year;
        return `${fyStartYear}-04-01`;
    };

    const getCurrentDate = () => new Date().toISOString().split('T')[0];

    const [toDate, setToDate] = useState(getCurrentDate());
    const [fromDate, setFromDate] = useState(getFinancialYearStart(getCurrentDate()));

    useEffect(() => {
        if (toDate) {
            setFromDate(getFinancialYearStart(toDate));
        }
    }, [toDate]);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (companyId && branchId && token) {
            fetchTrialBalanceAdvanced();
        }
    }, [companyId, branchId, token]);

    const fetchTrialBalanceAdvanced = async () => {
        if (fromDate && toDate && new Date(fromDate) > new Date(toDate)) {
            setValidationMsg('From Date cannot be greater than To Date.');
            setValidationOpen(true);
            return;
        }
        setDataItem([]);
        const loadingTimeout = setTimeout(() => {
            setLoading(true);
            setLoadingMessage('Loading Trial Balance Advanced...');
        }, 300);

        try {
            const response = await fetchData(
                'ReportApi',
                `/TrailBalanceAdvancedReport/${companyId}/${branchId}/${fromDate}/${toDate}`,
                token
            );
            if (response.responseCode === '1111') {
                setDataItem(response.response_data || []);
            } else {
                setDataItem(response.response_data || response || []);
            }
        } catch (err) {
            console.error('Error fetching Trial Balance Advanced:', err);
        } finally {
            clearTimeout(loadingTimeout);
            setLoading(false);
        }
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const totalOpening = filteredDataItem.reduce((sum, item) => sum + (parseFloat(item.Opening) || 0), 0);
    const totalDebit   = filteredDataItem.reduce((sum, item) => sum + (parseFloat(item.Debit)   || 0), 0);
    const totalCredit  = filteredDataItem.reduce((sum, item) => sum + (parseFloat(item.Credit)  || 0), 0);
    const totalClosing = filteredDataItem.reduce((sum, item) => sum + (parseFloat(item.Closing) || 0), 0);

    const formatCurrency = (value) => {
        const num = parseFloat(value) || 0;
        return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    // Group data by headName
    const getGroupedData = () => {
        const groups = [];
        let currentGroup = null;
        filteredDataItem.forEach((item) => {
            if (!currentGroup || currentGroup.headName !== item.headName) {
                currentGroup = { headName: item.headName, headid: item.headid, items: [] };
                groups.push(currentGroup);
            }
            currentGroup.items.push(item);
        });
        return groups;
    };

    const handleExportExcel = async () => {
        if (!filteredDataItem.length) return;

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Trial Balance Advanced');
        worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 4 }];

        const now = new Date();
        const formattedDateTime = `${now.toLocaleDateString()} ${now.toLocaleTimeString()}`;
        const fileName = `Trial_Balance_Advanced_${formatDate(fromDate).replace(/\//g, '-')}_To_${formatDate(toDate).replace(/\//g, '-')}.xlsx`;

        const thinBorder = {
            top: { style: 'thin' }, left: { style: 'thin' },
            bottom: { style: 'thin' }, right: { style: 'thin' }
        };

        const COLS = 6;

        // Row 1 - Company Name
        worksheet.mergeCells(`A1:F1`);
        const companyCell = worksheet.getCell('A1');
        companyCell.value = companyName || 'Company Name';
        companyCell.font = { bold: true, size: 14, color: { argb: 'FF1a237e' } };
        companyCell.alignment = { vertical: 'middle', horizontal: 'center' };
        companyCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFe8eaf6' } };
        worksheet.getRow(1).height = 24;

        // Row 2 - Report Title & Date Range
        worksheet.mergeCells(`A2:F2`);
        const titleCell = worksheet.getCell('A2');
        titleCell.value = `Trial Balance Advanced — From: ${formatDate(fromDate)}  To: ${formatDate(toDate)}`;
        titleCell.font = { bold: true, size: 11, color: { argb: 'FF37474f' } };
        titleCell.alignment = { vertical: 'middle', horizontal: 'center' };

        // Row 3 - Generated info
        worksheet.mergeCells(`A3:F3`);
        const infoCell = worksheet.getCell('A3');
        infoCell.value = `Generated On: ${formattedDateTime} | By: ${username}`;
        infoCell.font = { italic: true, size: 9, color: { argb: 'FF757575' } };
        infoCell.alignment = { vertical: 'middle', horizontal: 'center' };

        // Row 4 - Column Headers
        const headers = ['Head', 'Account Name', 'Opening (₹)', 'Debit (₹)', 'Credit (₹)', 'Closing (₹)'];
        const headerColors = ['FF1a237e', 'FF1a237e', 'FF4a148c', 'FFb71c1c', 'FF1b5e20', 'FFe65100'];
        const headerRow = worksheet.addRow(headers);
        headerRow.height = 20;
        headerRow.eachCell((cell, colNumber) => {
            cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } };
            cell.alignment = { vertical: 'middle', horizontal: colNumber <= 2 ? 'center' : 'right' };
            cell.border = thinBorder;
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: headerColors[colNumber - 1] } };
        });

        // Data rows grouped by headName
        const groupedData = getGroupedData();
        groupedData.forEach((group) => {
            // Group header row
            const groupRow = worksheet.addRow([group.headName, '', '', '', '', '']);
            worksheet.mergeCells(groupRow.number, 1, groupRow.number, COLS);
            groupRow.eachCell((cell) => {
                cell.font = { bold: true, size: 10, color: { argb: 'FF1a237e' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFe8eaf6' } };
                cell.alignment = { vertical: 'middle', horizontal: 'left' };
                cell.border = thinBorder;
            });

            group.items.forEach((item, idx) => {
                const row = worksheet.addRow([
                    '',
                    item.AccountName || '',
                    parseFloat(item.Opening) || 0,
                    parseFloat(item.Debit)   || 0,
                    parseFloat(item.Credit)  || 0,
                    parseFloat(item.Closing) || 0,
                ]);
                row.eachCell((cell, colNumber) => {
                    cell.border = thinBorder;
                    if (colNumber === 2) {
                        cell.font = { size: 10 };
                        cell.alignment = { horizontal: 'left', vertical: 'middle', indent: 2 };
                    }
                    if (colNumber >= 3) {
                        cell.alignment = { horizontal: 'right', vertical: 'middle' };
                        cell.numFmt = '₹#,##0.00';
                        const colorMap = {
                            3: parseFloat(item.Opening) !== 0 ? 'FF6a1b9a' : 'FFbdbdbd',
                            4: parseFloat(item.Debit)   > 0   ? 'FFc62828' : 'FFbdbdbd',
                            5: parseFloat(item.Credit)  > 0   ? 'FF2e7d32' : 'FFbdbdbd',
                            6: parseFloat(item.Closing) !== 0 ? 'FFbf360c' : 'FFbdbdbd',
                        };
                        cell.font = { size: 10, color: { argb: colorMap[colNumber] } };
                    }
                    if (idx % 2 === 1) {
                        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFfafbfe' } };
                    }
                });
            });
        });

        // Grand Total row
        const totRow = worksheet.addRow(['', 'Grand Total', totalOpening, totalDebit, totalCredit, totalClosing]);
        totRow.eachCell((cell, colNumber) => {
            cell.font = { bold: true, size: 11, color: { argb: 'FFFFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0d47a1' } };
            cell.border = thinBorder;
            if (colNumber >= 3) {
                cell.alignment = { horizontal: 'right', vertical: 'middle' };
                cell.numFmt = '₹#,##0.00';
            } else {
                cell.alignment = { horizontal: 'left', vertical: 'middle' };
            }
        });
        worksheet.mergeCells(totRow.number, 1, totRow.number, 2);

        worksheet.columns = [
            { width: 22 }, { width: 30 }, { width: 18 }, { width: 18 }, { width: 18 }, { width: 18 }
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), fileName);
    };

    const styles = {
        container: {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            background: '#f4f6f9',
            fontFamily: "'Segoe UI', 'Roboto', sans-serif",
            boxSizing: 'border-box',
            overflow: 'hidden'
        },
        card: {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background: '#ffffff',
            borderRadius: 0,
            overflow: 'hidden',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)'
        },
        toolbar: {
            display: 'flex',
            alignItems: 'center',
            padding: isMobile ? '6px 10px' : '7px 16px',
            background: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY} 100%)`,
            gap: 8,
            flexWrap: 'nowrap',
            overflowX: 'auto',
            scrollbarWidth: 'none',
            flexShrink: 0,
            boxShadow: '0 2px 8px rgba(13,71,161,0.3)'
        },
        dateInput: {
            height: 28,
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: 4,
            padding: '0 8px',
            fontSize: 11,
            backgroundColor: 'rgba(255,255,255,0.12)',
            color: '#fff',
            colorScheme: 'dark',
            flexShrink: 0,
            outline: 'none',
            transition: 'border-color 0.2s, background-color 0.2s'
        },
        searchInput: {
            height: 28,
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: 4,
            padding: '0 10px',
            fontSize: 11,
            backgroundColor: 'rgba(255,255,255,0.12)',
            color: '#fff',
            colorScheme: 'dark',
            flexShrink: 0,
            width: isMobile ? 100 : 140,
            outline: 'none',
            transition: 'border-color 0.2s, background-color 0.2s'
        },
        tableContainer: {
            flex: 1,
            overflow: 'auto',
            scrollbarWidth: 'thin',
            position: 'relative'
        },
        table: {
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
            fontSize: isMobile ? 11 : 12.5
        },
        thBase: {
            position: 'sticky',
            top: 0,
            zIndex: 10,
            color: '#ffffff',
            fontWeight: 700,
            fontSize: isMobile ? 9 : 10,
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
            padding: isMobile ? '5px 8px' : '6px 14px',
            whiteSpace: 'nowrap',
        },
        groupHeader: {
            background: 'linear-gradient(90deg, #e8eaf6 0%, #f3f4fd 100%)',
            borderBottom: '1px solid #c5cae9',
            borderTop: '1px solid #e8eaf6'
        },
        groupHeaderText: {
            padding: isMobile ? '3px 8px' : '4px 14px',
            fontSize: isMobile ? 10.5 : 11.5,
            fontWeight: 700,
            color: '#1a237e',
            letterSpacing: '0.2px',
            textTransform: 'capitalize'
        },
        dataRow: {
            borderBottom: '1px solid #f0f0f0',
            transition: 'background-color 0.15s ease'
        },
        accountCell: {
            padding: isMobile ? '2px 8px 2px 20px' : '3px 14px 3px 32px',
            fontSize: isMobile ? 11 : 12,
            color: '#37474f',
            fontWeight: 400,
            borderRight: '1px solid #f5f5f5'
        },
        amountCell: {
            padding: isMobile ? '2px 8px' : '3px 14px',
            fontSize: isMobile ? 11 : 12,
            fontWeight: 600,
            textAlign: 'right',
            fontFamily: "'Consolas', 'Courier New', monospace",
            whiteSpace: 'nowrap',
            borderRight: '1px solid #f5f5f5'
        },
        totalRow: {
            background: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, #1565c0 100%)`,
            flexShrink: 0
        },
        totalLabel: {
            padding: isMobile ? '5px 8px' : '6px 14px',
            fontSize: isMobile ? 11 : 12,
            fontWeight: 700,
            color: '#ffffff',
            letterSpacing: '0.3px'
        },
        totalAmount: {
            padding: isMobile ? '5px 8px' : '6px 14px',
            fontSize: isMobile ? 12 : 13,
            fontWeight: 700,
            textAlign: 'right',
            fontFamily: "'Consolas', 'Courier New', monospace",
            whiteSpace: 'nowrap'
        },
        emptyState: {
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '60px 20px',
            color: '#90a4ae'
        }
    };

    const thParticulars = { ...styles.thBase, background: HEADER_PARTICULARS, textAlign: 'left',  borderBottom: '2px solid #283593' };
    const thOpening     = { ...styles.thBase, background: HEADER_OPENING,     textAlign: 'right', borderBottom: '2px solid #6a1b9a' };
    const thDebit       = { ...styles.thBase, background: HEADER_DEBIT,       textAlign: 'right', borderBottom: '2px solid #c62828' };
    const thCredit      = { ...styles.thBase, background: HEADER_CREDIT,      textAlign: 'right', borderBottom: '2px solid #2e7d32' };
    const thClosing     = { ...styles.thBase, background: HEADER_CLOSING,     textAlign: 'right', borderBottom: '2px solid #bf360c' };

    const colW = isMobile
        ? { particulars: '34%', opening: '16%', debit: '16%', credit: '16%', closing: '18%' }
        : { particulars: '36%', opening: '16%', debit: '16%', credit: '16%', closing: '16%' };

    return (
        <div style={styles.container}>
            <LoadingOverlay open={loading} message={loadingMessage} />

            {/* Validation Modal */}
            <Dialog
                open={validationOpen}
                onClose={() => setValidationOpen(false)}
                PaperProps={{
                    sx: { borderRadius: 3, minWidth: 340, p: 0, overflow: 'hidden' }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    background: 'linear-gradient(135deg, #ff5252 0%, #d32f2f 100%)',
                    color: '#fff', py: 1.5, px: 2.5, fontSize: 16, fontWeight: 700
                }}>
                    <ErrorOutline sx={{ fontSize: 22 }} />
                    Validation Error
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
                    <Typography sx={{ fontSize: 14, color: '#37474f', fontWeight: 500 }}>
                        {validationMsg}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pb: 2 }}>
                    <Button
                        onClick={() => setValidationOpen(false)}
                        variant="contained"
                        sx={{
                            textTransform: 'none', borderRadius: 2, px: 3, fontWeight: 600,
                            background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                            '&:hover': { background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)' }
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <div style={styles.card}>
                {/* Toolbar */}
                <div style={styles.toolbar}>
                    <Tooltip title="Back" arrow>
                        <IconButton
                            onClick={() => history.push('/ReportsHub')}
                            size="small"
                            sx={{ color: '#fff', flexShrink: 0, p: 0.4, '&:hover': { background: 'rgba(255,255,255,0.15)' } }}
                        >
                            <ArrowBack sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0, letterSpacing: '0.3px' }}>
                        Trial Balance Advanced
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    <input
                        type="date" value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        style={styles.dateInput}
                    />
                    <input
                        type="date" value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        style={styles.dateInput}
                    />
                    <input
                        type="text" placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={styles.searchInput}
                    />
                    <Tooltip title="Refresh" arrow>
                        <IconButton
                            onClick={fetchTrialBalanceAdvanced}
                            size="small"
                            sx={{ color: '#fff', flexShrink: 0, p: 0.4, '&:hover': { background: 'rgba(255,255,255,0.15)' } }}
                        >
                            <Refresh sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton
                            onClick={handleExportExcel}
                            size="small"
                            sx={{ color: '#fff', flexShrink: 0, p: 0.4, '&:hover': { background: 'rgba(255,255,255,0.15)' } }}
                        >
                            <FileDownload sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Scrollable Table */}
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ ...thParticulars, width: colW.particulars }}>Particulars</th>
                                <th style={{ ...thOpening,     width: colW.opening    }}>Opening (₹)</th>
                                <th style={{ ...thDebit,       width: colW.debit      }}>Debit (₹)</th>
                                <th style={{ ...thCredit,      width: colW.credit     }}>Credit (₹)</th>
                                <th style={{ ...thClosing,     width: colW.closing    }}>Closing (₹)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDataItem.length === 0 && !loading ? (
                                <tr>
                                    <td colSpan={5} style={styles.emptyState}>
                                        <Typography sx={{ fontSize: 14, color: '#90a4ae', fontWeight: 500 }}>
                                            No data available
                                        </Typography>
                                        <Typography sx={{ fontSize: 11, color: '#b0bec5', mt: 0.5 }}>
                                            Adjust dates or refresh to load data
                                        </Typography>
                                    </td>
                                </tr>
                            ) : (
                                getGroupedData().map((group, gIdx) => (
                                    <React.Fragment key={gIdx}>
                                        {/* Group Header */}
                                        <tr style={styles.groupHeader}>
                                            <td colSpan={5} style={styles.groupHeaderText}>
                                                {group.headName}
                                            </td>
                                        </tr>
                                        {/* Account Rows */}
                                        {group.items.map((item, idx) => (
                                            <tr
                                                key={item.headid ? `${item.headid}-${idx}` : `${gIdx}-${idx}`}
                                                style={{
                                                    ...styles.dataRow,
                                                    backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fafbfe'
                                                }}
                                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#e3f2fd'; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#ffffff' : '#fafbfe'; }}
                                            >
                                                <td style={styles.accountCell}>{item.AccountName}</td>
                                                <td style={{ ...styles.amountCell, color: parseFloat(item.Opening) !== 0 ? '#6a1b9a' : '#9e9e9e' }}>
                                                    {parseFloat(item.Opening) !== 0 ? formatCurrency(item.Opening) : '-'}
                                                </td>
                                                <td style={{ ...styles.amountCell, color: parseFloat(item.Debit) > 0 ? '#c62828' : '#9e9e9e' }}>
                                                    {parseFloat(item.Debit) > 0 ? formatCurrency(item.Debit) : '-'}
                                                </td>
                                                <td style={{ ...styles.amountCell, color: parseFloat(item.Credit) > 0 ? '#2e7d32' : '#9e9e9e' }}>
                                                    {parseFloat(item.Credit) > 0 ? formatCurrency(item.Credit) : '-'}
                                                </td>
                                                <td style={{ ...styles.amountCell, color: parseFloat(item.Closing) !== 0 ? '#bf360c' : '#9e9e9e' }}>
                                                    {parseFloat(item.Closing) !== 0 ? formatCurrency(item.Closing) : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Grand Total — Fixed at bottom */}
                {filteredDataItem.length > 0 && (
                    <table style={{ ...styles.table, borderCollapse: 'collapse', flexShrink: 0 }}>
                        <tfoot>
                            <tr style={styles.totalRow}>
                                <td style={{ ...styles.totalLabel, width: colW.particulars }}>Grand Total</td>
                                <td style={{ ...styles.totalAmount, width: colW.opening, color: '#ce93d8' }}>{formatCurrency(totalOpening)}</td>
                                <td style={{ ...styles.totalAmount, width: colW.debit,   color: '#ffcdd2' }}>{formatCurrency(totalDebit)}</td>
                                <td style={{ ...styles.totalAmount, width: colW.credit,  color: '#a5d6a7' }}>{formatCurrency(totalCredit)}</td>
                                <td style={{ ...styles.totalAmount, width: colW.closing, color: '#ffccbc' }}>{formatCurrency(totalClosing)}</td>
                            </tr>
                        </tfoot>
                    </table>
                )}
            </div>
        </div>
    );
}

export default TrialBalanceAdvancedReport;
